import React, { Fragment, useCallback, useState } from "react";
import { Alert, Button, Card, CardBody, Col, Container, Row, Spinner } from "reactstrap";
import { Breadcrumbs } from "../../../AbstractElements";
import {
  fetchBalanceSheet,
  fetchBudgetVsActual,
  fetchGeneralLedger,
  fetchIncomeStatement,
  fetchTrialBalance,
  fetchVatSummary,
} from "../../../api/accounting";
import { formatApiError } from "../Common/credoraToast";
import AccountingReportFilters from "../Accounting/AccountingReportFilters";
import { useAccountingReportFilters } from "../Accounting/useAccountingReportFilters";
import { ALL_FINANCE_REPORTS } from "./financeReportConfig";
import {
  exportBalanceSheetCsv,
  exportBudgetVsActualCsv,
  exportGeneralLedgerCsv,
  exportIncomeStatementCsv,
  exportTrialBalanceCsv,
  exportVatSummaryCsv,
} from "./financeReportExport";

const EXPORTABLE = [
  {
    key: "trial-balance",
    title: "Trial Balance",
    path: "reports/finance/trial-balance",
    export: exportTrialBalanceCsv,
    fetch: fetchTrialBalance,
  },
  {
    key: "balance-sheet",
    title: "Balance Sheet",
    path: "reports/finance/balance-sheet",
    export: exportBalanceSheetCsv,
    fetch: fetchBalanceSheet,
  },
  {
    key: "income-statement",
    title: "Income Statement",
    path: "reports/finance/income-statement",
    export: exportIncomeStatementCsv,
    fetch: fetchIncomeStatement,
  },
  {
    key: "general-ledger",
    title: "General Ledger",
    path: "reports/finance/general-ledger",
    export: (lines) => exportGeneralLedgerCsv(lines),
    fetch: (params) => fetchGeneralLedger({ ...params, limit: 500 }),
  },
  {
    key: "budget-vs-actual",
    title: "Budget vs Actual",
    path: "reports/finance/budget-vs-actual",
    export: exportBudgetVsActualCsv,
    fetch: (params) =>
      params.branch_id && params.period_id
        ? fetchBudgetVsActual(params.branch_id, params.period_id)
        : Promise.reject(new Error("Branch and period required")),
  },
  {
    key: "vat-summary",
    title: "VAT Summary",
    path: "reports/finance/vat-summary",
    export: exportVatSummaryCsv,
    fetch: fetchVatSummary,
  },
];

const FinanceReportsExportPage = () => {
  const filters = useAccountingReportFilters();
  const [busyKey, setBusyKey] = useState("");
  const [error, setError] = useState("");
  const [lastExport, setLastExport] = useState("");

  const exportOne = useCallback(
    async (item) => {
      setBusyKey(item.key);
      setError("");
      setLastExport("");
      try {
        const params = filters.reportParams();
        const data = await item.fetch(params);
        item.export(data);
        setLastExport(item.title);
      } catch (err) {
        setError(formatApiError(err, `Failed to export ${item.title}.`));
      } finally {
        setBusyKey("");
      }
    },
    [filters.reportParams]
  );

  const exportAll = useCallback(async () => {
    setError("");
    setLastExport("");
    for (const item of EXPORTABLE) {
      try {
        const params = filters.reportParams();
        const data = await item.fetch(params);
        item.export(data);
      } catch {
        // Skip reports that need missing params (e.g. budget without period)
      }
    }
    setLastExport("All available reports");
  }, [filters.reportParams]);

  return (
    <Fragment>
      <Breadcrumbs mainTitle="Reports" parent="Financial Reports" title="Export CSV" />
      <Container fluid className="credora-finance-reports pb-5 mb-3">
        {error && <Alert color="danger">{error}</Alert>}
        {lastExport && <Alert color="success">Downloaded: {lastExport}</Alert>}

        <p className="text-muted">
          Export accounting reports as CSV using the branch and period filters below. PDF export can be added later;
          use your browser print dialog on individual report pages in the meantime.
        </p>

        <AccountingReportFilters {...filters} onApply={() => {}} loading={Boolean(busyKey)} />

        <div className="d-flex flex-wrap gap-2 mb-3">
          <Button color="primary" size="sm" onClick={exportAll} disabled={Boolean(busyKey)}>
            Export all available
          </Button>
        </div>

        <Row className="g-3">
          {EXPORTABLE.map((item) => (
            <Col key={item.key} md="6" lg="4">
              <Card className="h-100 border shadow-none">
                <CardBody className="d-flex flex-column">
                  <h6 className="mb-1">{item.title}</h6>
                  <p className="text-muted small flex-grow-1">
                    {ALL_FINANCE_REPORTS.find((r) => r.path === item.path)?.description || "CSV export"}
                  </p>
                  <Button
                    color="outline-primary"
                    size="sm"
                    className="align-self-start"
                    disabled={Boolean(busyKey)}
                    onClick={() => exportOne(item)}
                  >
                    {busyKey === item.key ? <Spinner size="sm" /> : "Download CSV"}
                  </Button>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      </Container>
    </Fragment>
  );
};

export default FinanceReportsExportPage;
